import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Button, IconButton, ToggleButton, ToggleButtonGroup,
  Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Switch,
  FormControlLabel, LinearProgress, ButtonBase, Tooltip, Link, useMediaQuery, Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CalendarMonth, ChevronLeft, ChevronRight, Add, Block, Google, OpenInNew, EventAvailable,
  Today, Delete, Forum,
} from '@mui/icons-material';
import { api } from '../config/api';
import { leerError } from '../utils/errores';

// ─── Utilidades de fecha (hora local del navegador) ─────────────────────────
type Vista = 'dia' | 'semana' | 'mes';
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const hm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const inicioDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sumarDias = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const lunesDe = (d: Date) => sumarDias(inicioDia(d), -((d.getDay() + 6) % 7));
const mismoDia = (a: Date, b: Date) => ymd(a) === ymd(b);
const hora = (d: Date) => d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
const diaLargo = (d: Date) => d.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' }).replace(/^([^\s,]+),\s*/, '$1 ');
const DIAS_CORTOS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function rangoDe(vista: Vista, ref: Date): { desde: Date; hasta: Date } {
  if (vista === 'dia') return { desde: inicioDia(ref), hasta: sumarDias(inicioDia(ref), 1) };
  if (vista === 'semana') { const l = lunesDe(ref); return { desde: l, hasta: sumarDias(l, 7) }; }
  const l = lunesDe(new Date(ref.getFullYear(), ref.getMonth(), 1));
  return { desde: l, hasta: sumarDias(l, 42) };
}

function etiquetaRango(vista: Vista, ref: Date): string {
  if (vista === 'dia') return diaLargo(ref);
  if (vista === 'mes') return ref.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
  const l = lunesDe(ref);
  const d = sumarDias(l, 6);
  const mesL = l.toLocaleDateString('es-CL', { month: 'long' });
  const mesD = d.toLocaleDateString('es-CL', { month: 'long' });
  return mesL === mesD ? `${l.getDate()} al ${d.getDate()} de ${mesD} de ${d.getFullYear()}` : `${l.getDate()} de ${mesL} al ${d.getDate()} de ${mesD} de ${d.getFullYear()}`;
}

// ─── Modelo de items de la agenda ────────────────────────────────────────────
type Tipo = 'cita' | 'google' | 'bloqueo';
interface Item {
  key: string; tipo: Tipo; inicio: Date; fin: Date; titulo: string; estado?: string; todoElDia?: boolean; raw: any;
}

const ESTILO: Record<string, { color: string; bg: string; label: string }> = {
  confirmada: { color: '#15803D', bg: '#DCFCE7', label: 'Confirmada' },
  pendiente: { color: '#B45309', bg: '#FEF3C7', label: 'Pendiente' },
  cancelada: { color: '#64748B', bg: '#F1F5F9', label: 'Cancelada' },
  google: { color: '#1A73E8', bg: '#E8F0FE', label: 'Google Calendar' },
  bloqueo: { color: '#B91C1C', bg: '#FEE2E2', label: 'Bloqueado' },
};
const estiloDe = (i: Item) => ESTILO[i.tipo === 'cita' ? (i.estado || 'confirmada') : i.tipo];

const ocurreEn = (i: Item, dia: Date) => i.inicio < sumarDias(inicioDia(dia), 1) && i.fin > inicioDia(dia);
const horario = (i: Item) => (i.todoElDia ? 'Todo el día' : `${hora(i.inicio)} – ${hora(i.fin)}`);

const EventoChip: React.FC<{ item: Item; onClick: () => void; compacto?: boolean }> = ({ item, onClick, compacto }) => {
  const e = estiloDe(item);
  const cancelada = item.tipo === 'cita' && item.estado === 'cancelada';
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`${e.label}: ${item.titulo}, ${horario(item)}`}
      sx={{
        display: 'block', width: '100%', textAlign: 'left', borderRadius: 1, bgcolor: e.bg,
        borderLeft: `3px solid ${e.color}`, px: compacto ? 0.6 : 1, py: compacto ? 0.2 : 0.6, mb: 0.5,
        '&:hover': { filter: 'brightness(0.96)' }, '&:focus-visible': { outline: `2px solid ${e.color}` },
      }}
    >
      <Typography variant="caption" component="div" noWrap sx={{ fontWeight: 700, color: e.color, lineHeight: 1.3, textDecoration: cancelada ? 'line-through' : 'none' }}>
        {item.todoElDia ? '' : `${hora(item.inicio)} `}{item.titulo}
      </Typography>
      {!compacto && (
        <Typography variant="caption" component="div" noWrap sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
          {horario(item)}{item.tipo === 'google' ? ' · Google' : ''}{item.tipo === 'bloqueo' ? ' · No disponible' : ''}
        </Typography>
      )}
    </ButtonBase>
  );
};

// ─── Formularios ─────────────────────────────────────────────────────────────
interface FormCita {
  id?: number; nombre_cliente: string; telefono_cliente: string; titulo: string;
  fecha: string; hora: string; duracion_min: number; notas: string; estado: string;
}

const CitaDialog: React.FC<{
  abierto: boolean; inicial: FormCita | null; cita: any | null; onCerrar: () => void; onGuardado: () => void;
}> = ({ abierto, inicial, cita, onCerrar, onGuardado }) => {
  const navigate = useNavigate();
  const [f, setF] = useState<FormCita | null>(inicial);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setF(inicial); setError(''); setAviso(''); }, [inicial]);
  if (!f) return null;
  const set = (k: keyof FormCita) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: k === 'duracion_min' ? parseInt(e.target.value, 10) || 0 : e.target.value });

  const guardar = async (forzar = false) => {
    setError(''); setAviso('');
    if (!f.nombre_cliente.trim()) return setError('Escribe el nombre del cliente.');
    if (!f.fecha || !f.hora) return setError('Indica la fecha y la hora.');
    const inicio = new Date(`${f.fecha}T${f.hora}:00`);
    if (isNaN(inicio.getTime())) return setError('La fecha u hora no es válida.');
    setGuardando(true);
    const body = {
      nombre_cliente: f.nombre_cliente, telefono_cliente: f.telefono_cliente, titulo: f.titulo, notas: f.notas,
      inicio: inicio.toISOString(), duracion_min: f.duracion_min, estado: f.estado, forzar,
    };
    try {
      if (f.id) await api.put(`/agenda/citas/${f.id}`, body);
      else await api.post('/agenda/citas', body);
      onGuardado();
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.codigo === 'NO_DISPONIBLE') setAviso(err.response.data.error);
      else setError(leerError(err, 'No se pudo guardar la cita.').message);
    } finally { setGuardando(false); }
  };

  const cancelar = async () => {
    if (!f.id || !window.confirm('¿Cancelar esta cita? Se quitará también de tu Google Calendar si estaba sincronizada.')) return;
    setGuardando(true);
    try { await api.delete(`/agenda/citas/${f.id}`); onGuardado(); }
    catch (err: any) { setError(leerError(err, 'No se pudo cancelar la cita.').message); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={abierto} onClose={onCerrar} fullWidth maxWidth="sm">
      <DialogTitle>{f.id ? 'Detalle de la cita' : 'Nueva cita'}</DialogTitle>
      <DialogContent>
        {cita && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip size="small" label={cita.origen === 'manual' ? 'Creada a mano' : 'Agendada por tu agente'} />
            {cita.google_link && (
              <Chip size="small" icon={<Google />} label="Ver en Google Calendar" component="a" href={cita.google_link} target="_blank" rel="noopener noreferrer" clickable />
            )}
            {cita.conversacion_id && (
              <Chip size="small" icon={<Forum />} label="Ir a conversaciones" onClick={() => navigate('/app/conversaciones')} clickable />
            )}
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {aviso && (
          <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => guardar(true)} disabled={guardando}>Agendar igual</Button>}>
            {aviso}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}><TextField label="Nombre del cliente" required fullWidth value={f.nombre_cliente} onChange={set('nombre_cliente')} inputProps={{ maxLength: 160 }} /></Grid>
          <Grid item xs={12} sm={6}><TextField label="Teléfono" fullWidth value={f.telefono_cliente} onChange={set('telefono_cliente')} inputProps={{ maxLength: 40 }} placeholder="+56 9 1234 5678" /></Grid>
          <Grid item xs={12}><TextField label="Motivo o título" fullWidth value={f.titulo} onChange={set('titulo')} inputProps={{ maxLength: 200 }} placeholder="Ej: Visita departamento, Evaluación, Corte de pelo" /></Grid>
          <Grid item xs={6} sm={4}><TextField label="Fecha" type="date" required fullWidth value={f.fecha} onChange={set('fecha')} InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={6} sm={4}><TextField label="Hora" type="time" required fullWidth value={f.hora} onChange={set('hora')} InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={12} sm={4}><TextField label="Duración (min)" type="number" fullWidth value={f.duracion_min} onChange={set('duracion_min')} inputProps={{ min: 5, max: 720, step: 5 }} /></Grid>
          {f.id && (
            <Grid item xs={12} sm={6}>
              <TextField select label="Estado" fullWidth value={f.estado} onChange={set('estado')}>
                <MenuItem value="confirmada">Confirmada</MenuItem>
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="cancelada">Cancelada</MenuItem>
              </TextField>
            </Grid>
          )}
          <Grid item xs={12}><TextField label="Notas" fullWidth multiline minRows={2} value={f.notas} onChange={set('notas')} inputProps={{ maxLength: 2000 }} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>{f.id && f.estado !== 'cancelada' && <Button color="error" startIcon={<Delete />} onClick={cancelar} disabled={guardando}>Cancelar cita</Button>}</Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onCerrar}>Cerrar</Button>
          <Button variant="contained" onClick={() => guardar(false)} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar'}</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const BloqueoDialog: React.FC<{ abierto: boolean; fechaInicial: string; onCerrar: () => void; onGuardado: () => void }> = ({ abierto, fechaInicial, onCerrar, onGuardado }) => {
  const [f, setF] = useState({ desde: fechaInicial, horaDesde: '13:00', hasta: fechaInicial, horaHasta: '14:00', todoElDia: false, motivo: '' });
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { if (abierto) { setF((x) => ({ ...x, desde: fechaInicial, hasta: fechaInicial })); setError(''); } }, [abierto, fechaInicial]);
  const guardar = async () => {
    setError('');
    const inicio = new Date(`${f.desde}T${f.todoElDia ? '00:00' : f.horaDesde}:00`);
    const fin = f.todoElDia ? sumarDias(new Date(`${f.hasta}T00:00:00`), 1) : new Date(`${f.hasta}T${f.horaHasta}:00`);
    if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) return setError('Revisa las fechas.');
    if (fin <= inicio) return setError('El término debe ser posterior al inicio.');
    setGuardando(true);
    try { await api.post('/agenda/bloqueos', { inicio: inicio.toISOString(), fin: fin.toISOString(), motivo: f.motivo }); onGuardado(); }
    catch (err: any) { setError(leerError(err, 'No se pudo bloquear el horario.').message); }
    finally { setGuardando(false); }
  };
  return (
    <Dialog open={abierto} onClose={onCerrar} fullWidth maxWidth="xs">
      <DialogTitle>Bloquear horario</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tu agente no ofrecerá ni agendará horas en este rango. Úsalo para vacaciones, feriados o la hora de almuerzo.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControlLabel control={<Switch checked={f.todoElDia} onChange={(e) => setF({ ...f, todoElDia: e.target.checked })} />} label="Días completos" sx={{ mb: 1 }} />
        <Grid container spacing={2}>
          <Grid item xs={f.todoElDia ? 12 : 7}><TextField label="Desde" type="date" fullWidth value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          {!f.todoElDia && <Grid item xs={5}><TextField label="Hora" type="time" fullWidth value={f.horaDesde} onChange={(e) => setF({ ...f, horaDesde: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>}
          <Grid item xs={f.todoElDia ? 12 : 7}><TextField label="Hasta" type="date" fullWidth value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          {!f.todoElDia && <Grid item xs={5}><TextField label="Hora" type="time" fullWidth value={f.horaHasta} onChange={(e) => setF({ ...f, horaHasta: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>}
          <Grid item xs={12}><TextField label="Motivo (opcional)" fullWidth value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} inputProps={{ maxLength: 200 }} placeholder="Ej: Vacaciones, Almuerzo" /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCerrar}>Cerrar</Button>
        <Button variant="contained" color="error" onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : 'Bloquear'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const DetalleDialog: React.FC<{ item: Item | null; onCerrar: () => void; onEliminado: () => void }> = ({ item, onCerrar, onEliminado }) => {
  const [borrando, setBorrando] = useState(false);
  if (!item) return null;
  const esGoogle = item.tipo === 'google';
  const quitar = async () => {
    setBorrando(true);
    try { await api.delete(`/agenda/bloqueos/${item.raw.id}`); onEliminado(); } finally { setBorrando(false); }
  };
  return (
    <Dialog open onClose={onCerrar} fullWidth maxWidth="xs">
      <DialogTitle>{esGoogle ? item.titulo : 'Horario bloqueado'}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1, textTransform: 'capitalize' }}>{diaLargo(item.inicio)}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {item.todoElDia ? 'Todo el día' : `${hora(item.inicio)} a ${mismoDia(item.inicio, item.fin) ? hora(item.fin) : `${diaLargo(item.fin)} ${hora(item.fin)}`}`}
        </Typography>
        {esGoogle ? (
          <Alert severity="info">
            Este evento viene de tu Google Calendar. {item.raw.ocupa ? 'Tu agente no ofrecerá esta hora a los clientes.' : 'Está marcado como "Disponible" en Google, así que no bloquea tu agenda.'} Para cambiarlo, ábrelo en Google.
          </Alert>
        ) : (
          <Typography variant="body2">{item.raw.motivo || 'Sin motivo'}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {esGoogle && item.raw.link && <Button startIcon={<OpenInNew />} href={item.raw.link} target="_blank" rel="noopener noreferrer">Abrir en Google</Button>}
        {!esGoogle && <Button color="error" startIcon={<Delete />} onClick={quitar} disabled={borrando}>Quitar bloqueo</Button>}
        <Button onClick={onCerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Página ──────────────────────────────────────────────────────────────────
const Agenda: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const movil = useMediaQuery(theme.breakpoints.down('md'));
  const [vista, setVista] = useState<Vista>(movil ? 'dia' : 'semana');
  const [ref, setRef] = useState<Date>(inicioDia(new Date()));
  const [data, setData] = useState<any>(null);
  const [slots, setSlots] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [verCanceladas, setVerCanceladas] = useState(false);
  const [formCita, setFormCita] = useState<{ form: FormCita; cita: any | null } | null>(null);
  const [bloqueoAbierto, setBloqueoAbierto] = useState(false);
  const [detalle, setDetalle] = useState<Item | null>(null);

  const { desde, hasta } = useMemo(() => rangoDe(vista, ref), [vista, ref]);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const { data: d } = await api.get('/agenda/eventos', { params: { desde: desde.toISOString(), hasta: hasta.toISOString() } });
      setData(d);
      if (vista === 'dia') {
        const s = await api.get('/agenda/disponibilidad', { params: { fecha: ymd(ref) } });
        setSlots(s.data.slots || []);
      } else setSlots([]);
    } catch (err: any) {
      setError(leerError(err, 'No se pudo cargar la agenda.').message);
    } finally { setCargando(false); }
  }, [desde, hasta, vista, ref]);
  useEffect(() => { cargar(); }, [cargar]);

  const items: Item[] = useMemo(() => {
    if (!data) return [];
    const out: Item[] = [];
    for (const c of data.citas || []) {
      if (c.estado === 'cancelada' && !verCanceladas) continue;
      out.push({ key: `c${c.id}`, tipo: 'cita', inicio: new Date(c.inicio), fin: new Date(c.fin), titulo: c.nombre_cliente || c.titulo || 'Cita', estado: c.estado, raw: c });
    }
    for (const b of data.bloqueos || []) out.push({ key: `b${b.id}`, tipo: 'bloqueo', inicio: new Date(b.inicio), fin: new Date(b.fin), titulo: b.motivo || 'Bloqueado', raw: b });
    for (const g of data.google?.eventos || []) out.push({ key: `g${g.id}`, tipo: 'google', inicio: new Date(g.inicio), fin: new Date(g.fin), titulo: g.titulo, todoElDia: g.todoElDia, raw: g });
    return out.sort((a, b) => a.inicio.getTime() - b.inicio.getTime());
  }, [data, verCanceladas]);

  const mover = (dir: number) => {
    if (vista === 'dia') setRef(sumarDias(ref, dir));
    else if (vista === 'semana') setRef(sumarDias(ref, 7 * dir));
    else setRef(new Date(ref.getFullYear(), ref.getMonth() + dir, 1));
  };
  const irADia = (d: Date) => { setRef(inicioDia(d)); setVista('dia'); };

  const duracion = data?.config?.duracion_cita_min || 30;
  const nuevaCita = (fecha = ymd(ref), h = '10:00') => setFormCita({
    form: { nombre_cliente: '', telefono_cliente: '', titulo: '', fecha, hora: h, duracion_min: duracion, notas: '', estado: 'confirmada' },
    cita: null,
  });
  const abrir = (i: Item) => {
    if (i.tipo !== 'cita') return setDetalle(i);
    const c = i.raw;
    setFormCita({
      form: {
        id: c.id, nombre_cliente: c.nombre_cliente || '', telefono_cliente: c.telefono_cliente || '', titulo: c.titulo || '',
        fecha: ymd(i.inicio), hora: hm(i.inicio), duracion_min: Math.max(5, Math.round((i.fin.getTime() - i.inicio.getTime()) / 60000)),
        notas: c.notas || '', estado: c.estado,
      },
      cita: c,
    });
  };
  const alGuardar = () => { setFormCita(null); setBloqueoAbierto(false); setDetalle(null); cargar(); };

  const hoy = new Date();
  const dias7 = Array.from({ length: 7 }, (_, i) => sumarDias(desde, i));

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarMonth color="primary" />
          <Typography variant="h4">Agenda</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="error" startIcon={<Block />} onClick={() => setBloqueoAbierto(true)}>Bloquear horario</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => nuevaCita()}>Nueva cita</Button>
        </Stack>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Las citas que agenda tu agente y las que creas a mano. El agente solo ofrece horas libres dentro de tu horario de atención.
      </Typography>

      {/* Avisos de configuración */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        {data && !data.config?.tool_agendar && (
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => navigate('/app/orquestacion')}>Activar</Button>}>
            Tu agente todavía no agenda citas. Actívalo en Orquestación → "Permitir agendar citas".
          </Alert>
        )}
        {data?.config?.horario_por_defecto && (
          <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate('/app/orquestacion')}>Configurar</Button>}>
            No has definido tu horario de atención, así que el agente ofrece horas de lunes a viernes de 9:00 a 18:00.
          </Alert>
        )}
        {data && !data.google?.conectado && (
          <Alert severity="info" icon={<Google />} action={<Button color="inherit" size="small" onClick={() => navigate('/app/integraciones')}>Conectar</Button>}>
            Tu agenda funciona sin Google. Si conectas Google Calendar, verás aquí tus eventos y el agente no ofrecerá esas horas.
          </Alert>
        )}
        {data?.google?.conectado && data.google.error && (
          <Alert severity="warning">No pudimos leer tu Google Calendar en este momento. Tus citas de tRespondo se muestran igual.</Alert>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        {cargando && <LinearProgress />}
        <CardContent>
          {/* Barra de navegación */}
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Button size="small" variant="outlined" startIcon={<Today />} onClick={() => setRef(inicioDia(new Date()))}>Hoy</Button>
              <IconButton aria-label="Anterior" onClick={() => mover(-1)}><ChevronLeft /></IconButton>
              <IconButton aria-label="Siguiente" onClick={() => mover(1)}><ChevronRight /></IconButton>
              <Typography variant="h6" component="h2" sx={{ textTransform: 'capitalize', ml: 0.5, fontSize: { xs: '1rem', sm: '1.15rem' } }}>{etiquetaRango(vista, ref)}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              {data?.google?.conectado && (
                <Tooltip title={data.google.email || ''}><Chip size="small" icon={<Google />} label="Sincronizada con Google" color="primary" variant="outlined" /></Tooltip>
              )}
              <FormControlLabel control={<Switch size="small" checked={verCanceladas} onChange={(e) => setVerCanceladas(e.target.checked)} />} label={<Typography variant="body2">Canceladas</Typography>} />
              <ToggleButtonGroup size="small" exclusive value={vista} onChange={(_, v) => v && setVista(v)} aria-label="Vista de la agenda">
                <ToggleButton value="dia">Día</ToggleButton>
                <ToggleButton value="semana">Semana</ToggleButton>
                <ToggleButton value="mes">Mes</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          {/* Vista MES */}
          {vista === 'mes' && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
                {DIAS_CORTOS.map((d) => <Typography key={d} variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}>{d}</Typography>)}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid #E2E8F0', borderRadius: 1, overflow: 'hidden' }}>
                {Array.from({ length: 42 }, (_, i) => sumarDias(desde, i)).map((d) => {
                  const delDia = items.filter((it) => ocurreEn(it, d));
                  const fuera = d.getMonth() !== ref.getMonth();
                  const esHoy = mismoDia(d, hoy);
                  const max = movil ? 1 : 3;
                  return (
                    <Box key={ymd(d)} sx={{ minHeight: { xs: 64, md: 110 }, p: 0.5, borderRight: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', bgcolor: fuera ? '#F8FAFC' : 'white', minWidth: 0 }}>
                      <ButtonBase onClick={() => irADia(d)} aria-label={`Ver ${diaLargo(d)}`} sx={{ borderRadius: '50%', width: 26, height: 26, mb: 0.3, bgcolor: esHoy ? 'primary.main' : 'transparent', color: esHoy ? 'white' : fuera ? 'text.disabled' : 'text.primary' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{d.getDate()}</Typography>
                      </ButtonBase>
                      {delDia.slice(0, max).map((it) => <EventoChip key={it.key} item={it} compacto onClick={() => abrir(it)} />)}
                      {delDia.length > max && (
                        <Link component="button" variant="caption" onClick={() => irADia(d)} sx={{ display: 'block' }}>+{delDia.length - max} más</Link>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Vista SEMANA */}
          {vista === 'semana' && (
            <Grid container spacing={1} columns={{ xs: 1, md: 7 }}>
              {dias7.map((d) => {
                const delDia = items.filter((it) => ocurreEn(it, d));
                const esHoy = mismoDia(d, hoy);
                return (
                  <Grid item xs={1} key={ymd(d)}>
                    <Box sx={{ border: '1px solid', borderColor: esHoy ? 'primary.main' : '#E2E8F0', borderRadius: 1, p: 1, minHeight: { md: 260 }, height: '100%', bgcolor: 'white' }}>
                      <ButtonBase onClick={() => irADia(d)} sx={{ display: 'block', textAlign: 'left', width: '100%', mb: 1, borderRadius: 1 }} aria-label={`Ver ${diaLargo(d)}`}>
                        <Typography variant="caption" sx={{ color: esHoy ? 'primary.dark' : 'text.secondary', fontWeight: 700 }}>{DIAS_CORTOS[(d.getDay() + 6) % 7]}</Typography>
                        <Typography variant="h6" sx={{ lineHeight: 1, color: esHoy ? 'primary.dark' : 'text.primary' }}>{d.getDate()}</Typography>
                      </ButtonBase>
                      {delDia.map((it) => <EventoChip key={it.key} item={it} onClick={() => abrir(it)} />)}
                      {delDia.length === 0 && <Typography variant="caption" color="text.disabled">Sin citas</Typography>}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Vista DÍA: citas + horas libres */}
          {vista === 'dia' && (() => {
            const delDia = items.filter((it) => ocurreEn(it, ref));
            const libres = slots.filter((s) => s.libre).map((s) => ({ inicio: new Date(s.inicio), fin: new Date(s.fin) }));
            const filas: ({ t: 'item'; item: Item } | { t: 'libre'; inicio: Date; fin: Date })[] = [
              ...delDia.map((item) => ({ t: 'item' as const, item })),
              ...libres.map((l) => ({ t: 'libre' as const, ...l })),
            ].sort((a, b) => (a.t === 'item' ? a.item.inicio : a.inicio).getTime() - (b.t === 'item' ? b.item.inicio : b.inicio).getTime());
            return (
              <Box>
                {filas.length === 0 && !cargando && (
                  <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                    <EventAvailable sx={{ fontSize: 44, color: 'text.disabled' }} />
                    <Typography>No hay citas ni horas disponibles este día.</Typography>
                    <Typography variant="body2">Puede que no atiendas este día o que ya hayan pasado las horas.</Typography>
                  </Box>
                )}
                <Stack spacing={0.5}>
                  {filas.map((f) => f.t === 'item' ? (
                    <Stack key={f.item.key} direction="row" spacing={1.5} alignItems="flex-start">
                      <Typography variant="body2" sx={{ width: 52, flexShrink: 0, pt: 0.7, color: 'text.secondary', fontWeight: 600 }}>{f.item.todoElDia ? 'Día' : hora(f.item.inicio)}</Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}><EventoChip item={f.item} onClick={() => abrir(f.item)} /></Box>
                    </Stack>
                  ) : (
                    <Stack key={`l${f.inicio.getTime()}`} direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="body2" sx={{ width: 52, flexShrink: 0, color: 'text.secondary' }}>{hora(f.inicio)}</Typography>
                      <ButtonBase
                        onClick={() => nuevaCita(ymd(f.inicio), hm(f.inicio))}
                        aria-label={`Hora libre a las ${hora(f.inicio)}. Crear cita`}
                        sx={{ flex: 1, justifyContent: 'flex-start', border: '1px dashed #86EFAC', borderRadius: 1, px: 1, py: 0.5, mb: 0.5, color: '#15803D', '&:hover': { bgcolor: '#F0FDF4' } }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Libre · {hora(f.inicio)} – {hora(f.fin)} · Agendar</Typography>
                      </ButtonBase>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            );
          })()}

          {/* Leyenda */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {['confirmada', 'pendiente', 'google', 'bloqueo'].map((k) => (
              <Stack key={k} direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: ESTILO[k].bg, borderLeft: `3px solid ${ESTILO[k].color}` }} />
                <Typography variant="caption" color="text.secondary">{ESTILO[k].label}</Typography>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <CitaDialog abierto={!!formCita} inicial={formCita?.form || null} cita={formCita?.cita || null} onCerrar={() => setFormCita(null)} onGuardado={alGuardar} />
      <BloqueoDialog abierto={bloqueoAbierto} fechaInicial={ymd(ref)} onCerrar={() => setBloqueoAbierto(false)} onGuardado={alGuardar} />
      <DetalleDialog item={detalle} onCerrar={() => setDetalle(null)} onEliminado={alGuardar} />
    </Box>
  );
};

export default Agenda;
